import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import WineGlassIcon from './icons/WineGlassIcon';
import TenantBrand from './TenantBrand';
import { useTenant } from '../config/tenantConfig';

const Footer = () => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const tenant = useTenant();

  return (
    <footer className="bg-gradient-to-br from-[#1a1625] to-[#24202f] border-t border-gray-800/50 mt-auto shrink-0">
      {/* Rodapé Compacto (1 linha) */}
      <div className={`${isExpanded ? 'hidden' : 'block'}`}>
        <div className="max-w-7xl mx-auto px-4 py-3 tablet-landscape:py-2">
          {/* Logo Centralizada */}
          <div className="flex flex-col items-center justify-center gap-3 text-xs">
            {/* Logo do Tenant se existir */}
            <TenantBrand
              tenant={tenant}
              imageClassName="h-8 md:h-10 w-auto max-w-[160px] object-contain opacity-90"
              textClassName="font-serif text-wine-gold text-xs md:text-sm font-semibold"
              fallback={(
                <div className="flex items-center gap-2">
                  <div className="scale-[0.3] origin-center">
                    <WineGlassIcon />
                  </div>
                  <span className="font-serif text-wine-gold text-xs md:text-sm font-semibold">
                    Sommelier Agora
                  </span>
                </div>
              )}
            />
            
            {/* Links + Botões em linha */}
            <div className="flex items-center gap-2 md:gap-4">
              {/* Links de navegação - visível apenas em telas maiores */}
              <button 
                onClick={() => navigate('/foods')}
                className="hidden lg:block text-gray-400 hover:text-wine-gold transition-colors text-xs"
              >
                Harmonização
              </button>
              <button 
                onClick={() => navigate('/regions')}
                className="hidden lg:block text-gray-400 hover:text-wine-gold transition-colors text-xs"
              >
                Regiões
              </button>
              
              {/* Botão Início */}
              <button
                onClick={() => navigate('/')}
                className="px-2 md:px-3 py-1 md:py-1.5 bg-wine-gold/20 text-wine-gold rounded-lg hover:bg-wine-gold/30 transition-colors text-xs font-medium"
              >
                Início
              </button>

              {/* Botão Expandir */}
              <button
                onClick={() => setIsExpanded(true)}
                className="flex items-center gap-1 text-wine-gold hover:text-wine-gold/80 transition-colors font-medium text-xs"
              >
                <span className="hidden md:inline">Mais</span>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              
              <span className="text-gray-500 text-xs">
                © 2025
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Conteúdo Completo do Footer (Expandido) */}
      <div className={`${isExpanded ? 'block' : 'hidden'}`}>
        <div className="max-w-7xl mx-auto px-4 py-8 lg:py-12">
          {/* Botão para Recolher */}
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setIsExpanded(false)}
              className="flex items-center gap-2 text-wine-gold hover:text-wine-gold/80 transition-colors text-sm"
            >
              Recolher
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 15l7-7 7 7"
                />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* Logo e Descrição */}
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <TenantBrand
                  tenant={tenant}
                  imageClassName="h-16 md:h-20 w-auto max-w-[240px] object-contain"
                  textClassName="font-serif text-wine-gold text-2xl font-semibold"
                  fallback={(
                    <>
                      <div className="flex flex-col items-center">
                        <div className="mb-4 scale-75">
                          <WineGlassIcon />
                        </div>
                        <h3 className="font-serif text-wine-gold text-2xl font-semibold">
                          Sommelier Agora
                        </h3>
                      </div>
                    </>
                  )}
                />
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                Descubra o vinho perfeito para cada momento. 
              </p>
            </div>

            {/* Links Rápidos */}
            <div className="text-center">
              <h4 className="text-wine-gold font-semibold uppercase tracking-wide text-sm mb-4">
                Navegação
              </h4>
              <ul className="space-y-2">
                <li>
                  <button 
                    onClick={() => {
                      navigate('/');
                      setIsExpanded(false);
                    }}
                    className="text-gray-400 hover:text-wine-gold transition-colors text-sm"
                  >
                    🏠 Página Inicial
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => {
                      navigate('/foods');
                      setIsExpanded(false);
                    }}
                    className="text-gray-400 hover:text-wine-gold transition-colors text-sm"
                  >
                    Harmonização
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => {
                      navigate('/regions');
                      setIsExpanded(false);
                    }}
                    className="text-gray-400 hover:text-wine-gold transition-colors text-sm"
                  >
                    Regiões
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => {
                      navigate('/wines/grape');
                      setIsExpanded(false);
                    }}
                    className="text-gray-400 hover:text-wine-gold transition-colors text-sm"
                  >
                    Tipos de Uva
                  </button>
                </li>
              </ul>
            </div>

            {/* Informações da Empresa */}
            <div className="text-center md:text-right">
              <h4 className="text-wine-gold font-semibold uppercase tracking-wide text-sm mb-4">
                Contato
              </h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>📧 contato@sommelieragora.com.br</li>
                <li>📱 (34) 9678-9368</li>
                <li>📍 Uberlândia, Brasil</li>
              </ul>
              
              {/* Redes Sociais */}
              <div className="flex justify-center md:justify-end gap-4 mt-6">
                <button 
                  onClick={() => window.open('https://instagram.com/sommelieragora', '_blank')}
                  className="text-gray-400 hover:text-wine-gold transition-colors"
                  aria-label="Instagram"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </button>
                <button 
                  onClick={() => window.open('https://wa.me/5534996789368', '_blank')}
                  className="text-gray-400 hover:text-wine-gold transition-colors"
                  aria-label="WhatsApp"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-800/50 pt-6 lg:pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-500 text-xs lg:text-sm">
                © 2025 {tenant.name}. Todos os direitos reservados.
              </p>
              <div className="flex gap-4 lg:gap-6 text-xs text-gray-500">
                <button 
                  onClick={() => {}}
                  className="hover:text-wine-gold transition-colors"
                >
                  Política de Privacidade
                </button>
                <button 
                  onClick={() => {}}
                  className="hover:text-wine-gold transition-colors"
                >
                  Termos de Uso
                </button>
                <button 
                  onClick={() => {}}
                  className="hover:text-wine-gold transition-colors"
                >
                  Sobre Nós
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

